import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export type SavingsGoalStatus = 'active' | 'completed' | 'cancelled';

export interface SavingsGoal {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  target_amount: string;
  current_amount: string;
  target_date: string | null;
  status: SavingsGoalStatus;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateGoalInput {
  name: string;
  target_amount: number;
  description?: string;
  icon?: string;
  color?: string;
  target_date?: string;
}

export interface UpdateGoalInput {
  name?: string;
  target_amount?: number;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  target_date?: string | null;
  status?: SavingsGoalStatus;
}

export interface SavingsContribution {
  id: string;
  goal_id: string;
  user_id: string;
  amount: string;
  notes: string | null;
  contributed_at: string;
  created_at: string;
}

interface UseSavingsGoalsOptions {
  includeCompleted?: boolean;
}

export function useSavingsGoals(options?: UseSavingsGoalsOptions) {
  const { user, session, initialized } = useAuth();
  const queryClient = useQueryClient();

  const queryParams = new URLSearchParams();
  if (options?.includeCompleted) queryParams.set('includeCompleted', 'true');
  const queryString = queryParams.toString();
  const endpoint = `/personal/savings-goals${queryString ? `?${queryString}` : ''}`;

  const {
    data: goals,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['savingsGoals', user?.id, options?.includeCompleted],
    queryFn: async (): Promise<SavingsGoal[]> => {
      if (!session?.access_token) {
        return [];
      }

      const response = await api.get<{ goals: SavingsGoal[] }>(
        endpoint,
        session.access_token
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch savings goals');
      }

      return response.data?.goals ?? [];
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
    enabled: initialized && !!user && !!session?.access_token,
  });

  const invalidateGoals = () =>
    queryClient.invalidateQueries({ queryKey: ['savingsGoals'] });

  const createGoal = useMutation({
    mutationFn: async (input: CreateGoalInput) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.post<{ goal: SavingsGoal }>(
        '/personal/savings-goals',
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to create goal');
      return response.data!.goal;
    },
    onSuccess: invalidateGoals,
  });

  const updateGoal = useMutation({
    mutationFn: async ({ id, ...input }: UpdateGoalInput & { id: string }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.patch<{ goal: SavingsGoal }>(
        `/personal/savings-goals/${id}`,
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to update goal');
      return response.data!.goal;
    },
    onSuccess: invalidateGoals,
  });

  const deleteGoal = useMutation({
    mutationFn: async (id: string) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.delete<{ message: string }>(
        `/personal/savings-goals/${id}`,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to delete goal');
      return response.data!;
    },
    onSuccess: invalidateGoals,
  });

  const addContribution = useMutation({
    mutationFn: async ({ id, amount, notes, contributed_at }: {
      id: string; amount: number; notes?: string; contributed_at?: string;
    }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.post<{ goal: SavingsGoal }>(
        `/personal/savings-goals/${id}/contribute`,
        { amount, notes, contributed_at },
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to add contribution');
      return response.data!.goal;
    },
    onSuccess: () => {
      invalidateGoals();
      queryClient.invalidateQueries({ queryKey: ['savingsContributions'] });
    },
  });

  const updateContribution = useMutation({
    mutationFn: async ({ goalId, contributionId, ...input }: {
      goalId: string; contributionId: string;
      amount?: number; notes?: string | null; contributed_at?: string;
    }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.patch<{ contribution: SavingsContribution }>(
        `/personal/savings-goals/${goalId}/contributions/${contributionId}`,
        input,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to update contribution');
      return response.data!.contribution;
    },
    onSuccess: () => {
      invalidateGoals();
      queryClient.invalidateQueries({ queryKey: ['savingsContributions'] });
    },
  });

  const deleteContribution = useMutation({
    mutationFn: async ({ goalId, contributionId }: { goalId: string; contributionId: string }) => {
      if (!session?.access_token) throw new Error('Not authenticated');
      const response = await api.delete<{ message: string }>(
        `/personal/savings-goals/${goalId}/contributions/${contributionId}`,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to delete contribution');
      return response.data!;
    },
    onSuccess: () => {
      invalidateGoals();
      queryClient.invalidateQueries({ queryKey: ['savingsContributions'] });
    },
  });

  return {
    goals: goals ?? [],
    isLoading,
    error,
    refetch,
    createGoal,
    updateGoal,
    deleteGoal,
    addContribution,
    updateContribution,
    deleteContribution,
  };
}

export function useSavingsContributions(goalId: string | null) {
  const { session, initialized } = useAuth();

  const {
    data: contributions,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['savingsContributions', goalId],
    queryFn: async (): Promise<SavingsContribution[]> => {
      if (!session?.access_token || !goalId) return [];
      const response = await api.get<{ contributions: SavingsContribution[] }>(
        `/personal/savings-goals/${goalId}/contributions`,
        session.access_token
      );
      if (!response.success) throw new Error(response.error || 'Failed to fetch contributions');
      return response.data?.contributions ?? [];
    },
    staleTime: 1000 * 60 * 5,
    retry: 1,
    enabled: initialized && !!session?.access_token && !!goalId,
  });

  return {
    contributions: contributions ?? [],
    isLoading,
    error,
    refetch,
  };
}
